/**
 * Reading and writing context switches — the markers that say what a stretch
 * of the conversation was about.
 *
 * The record itself is a wire shape (`ContextSwitch` in services/agents/
 * config); this module is how the chat derives one from what is on screen and
 * turns one back into something a person reads.
 */

import type { ContextSwitch } from '@/services/agents/config';
import {
  CONTEXT_SWITCH_KINDS,
  type ContextSwitchKind,
} from '@/services/agents/context-switch-kinds';
import type { SurfaceContext } from './types';

export type { ContextSwitch, ContextSwitchKind };

/**
 * What makes two contexts the same context. Everything the agent is grounded
 * with — the canvas graph, a node's prompts — is excluded, so editing a
 * workflow does not read as switching away from it. Which node is selected
 * is not grounding but the subject itself, so it is its own context.
 *
 * Must stay in step with `_context_identity` in platform-api's agents route,
 * which dedupes the two writers against each other.
 */
/** Stands in for the id a canvas does not have until it is first saved. */
const UNSAVED_CANVAS_ID = 'new';

export const UNSAVED_CANVAS_IDENTITY = `workflow_canvas:${UNSAVED_CANVAS_ID}`;

/**
 * Whether a switch to this context is recorded only once the user says
 * something in it. The divider shows at once, but the row is written by the
 * chat endpoint with the first turn, or never: looking at something is not
 * talking about it. A review pause is the exception -- the exchange with a
 * paused node does not go through the chat endpoint, so its divider is
 * recorded when the pause happens.
 */
export function recordsOnSend(context: ContextSwitch): boolean {
  return context.kind !== 'node_chat';
}

export function contextIdentity(context: ContextSwitch): string {
  return context.kind === 'none'
    ? 'none'
    : `${context.kind}:${context.id ?? ''}`;
}

export interface ContextSwitchParts {
  /** Muted lead-in, e.g. "Chatting about". */
  lead: string;
  /** The thing itself, quoted and in full contrast. Absent when unresolved. */
  name?: string;
}

/**
 * The marker's label split the way the design sets it: a muted lead-in, then
 * the name in quotes at full contrast. Falls back to a lead-in that reads on
 * its own when the name never resolved.
 */
export function contextSwitchParts(context: ContextSwitch): ContextSwitchParts {
  const name = context.title?.trim() || undefined;
  switch (context.kind) {
    case 'artifact':
    case 'skill_doc':
      return name
        ? { lead: 'Chatting about', name }
        : { lead: 'Chatting about an artifact' };
    case 'workflow_canvas':
      return name
        ? { lead: 'Chatting about', name }
        : { lead: 'Chatting about a workflow' };
    case 'workflow_node':
      return name
        ? { lead: 'Chatting about node', name }
        : { lead: 'Chatting about a node' };
    case 'artifact_gallery':
      return name
        ? { lead: 'Chatting about artifacts from', name }
        : { lead: 'Chatting about artifacts' };
    case 'node_chat':
      return name
        ? { lead: 'Paused for review on', name }
        : { lead: 'Paused for review on a node' };
    case 'none':
      return { lead: 'Back to general chat' };
  }
}

/** The marker as one line -- what the chat shows, joined for plain text. */
export function contextSwitchLabel(context: ContextSwitch): string {
  const { lead, name } = contextSwitchParts(context);
  return name ? `${lead} ${name}` : lead;
}

export function contextFromSurface(
  surface: SurfaceContext | null | undefined,
  names: { workflowName?: string | null; workflowId?: string | null } = {},
): ContextSwitch {
  if (!surface) return { kind: 'none' };
  switch (surface.surface) {
    case 'artifact':
      return {
        kind: 'artifact',
        // Keyed on the lineage, not the row: an edit mints a new artifact id
        // for the same document, and that is not a change of subject.
        id: surface.context_key ?? surface.artifact_id,
        title: surface.title,
      };
    case 'skill_doc':
      return { kind: 'skill_doc', id: surface.artifact_id };
    case 'artifact_gallery':
      return {
        kind: 'artifact_gallery',
        id: surface.workflow_id,
        // The canvas store holds whichever workflow was last loaded, which is
        // only this gallery's workflow when the ids agree -- otherwise the
        // marker would name a workflow the user is not looking at.
        title:
          names.workflowId && names.workflowId === surface.workflow_id
            ? (names.workflowName ?? undefined)
            : undefined,
        workflow_id: surface.workflow_id,
      };
    case 'workflow_canvas':
      // A selected node is what the user is talking to: "change the task"
      // means that node's task. Deselecting returns to the workflow.
      if (surface.focused_node) {
        return {
          kind: 'workflow_node',
          id: surface.focused_node.node_id,
          title: surface.focused_node.node_display_name,
          workflow_id: surface.workflow_id,
        };
      }
      return {
        kind: 'workflow_canvas',
        // An unsaved canvas has no id yet, but it is still one context the
        // user stayed in -- key it so it doesn't re-announce on every edit.
        // Saving it mints the real id; the recorder adopts that silently
        // rather than reading it as a move to another workflow.
        id: surface.workflow_id ?? UNSAVED_CANVAS_ID,
        title: names.workflowName ?? undefined,
        workflow_id: surface.workflow_id,
      };
  }
}

export interface ContextSection<T> {
  key: string;
  context?: ContextSwitch;
  messages: T[];
}

/**
 * Split a transcript into the stretches each marker opened.
 *
 * Sectioning is what makes the markers sticky in the way the design asks for:
 * a marker can only hold the top of the window for as long as its own
 * containing block is on screen, so grouping is what lets the next one push
 * the last one out rather than overlap it.
 *
 * Messages before the first marker form a leading section with no marker —
 * conversations that predate this feature are entirely that section.
 */
export function groupMessagesByContext<
  T extends { _localId: string; _contextSwitch?: ContextSwitch },
>(messages: T[]): Array<ContextSection<T>> {
  const sections: Array<ContextSection<T>> = [];

  for (const message of messages) {
    if (message._contextSwitch) {
      const open = sections[sections.length - 1];
      // A marker with nothing said under it describes a stretch that never
      // happened, so the next one replaces it rather than stacking on top.
      // Switching back and forth while saying nothing leaves one marker --
      // the context you actually ended up in.
      if (open && open.context && open.messages.length === 0) {
        sections[sections.length - 1] = {
          key: message._localId,
          context: message._contextSwitch,
          messages: [],
        };
        continue;
      }
      sections.push({
        key: message._localId,
        context: message._contextSwitch,
        messages: [],
      });
      continue;
    }
    if (!sections.length) {
      sections.push({ key: 'lead', messages: [] });
    }
    sections[sections.length - 1].messages.push(message);
  }

  // A marker that names the context already in effect announces nothing --
  // it only survives collapsing above when a round trip left and came back
  // in silence (a pause that ended with nothing said under it). Whatever was
  // said after the return belongs to the stretch that was already open, so
  // the two stretches become one rather than the marker reading twice.
  return sections.reduce<Array<ContextSection<T>>>((merged, section) => {
    const previous = merged[merged.length - 1];
    if (
      section.context &&
      previous?.context &&
      contextIdentity(previous.context) === contextIdentity(section.context)
    ) {
      previous.messages.push(...section.messages);
      return merged;
    }
    merged.push(section);
    return merged;
  }, []);
}

function isKind(value: unknown): value is ContextSwitchKind {
  return (
    typeof value === 'string' &&
    CONTEXT_SWITCH_KINDS.includes(value as ContextSwitchKind)
  );
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined;
}

/**
 * The marker a persisted row stands for, or undefined for an ordinary turn.
 * An unrecognized payload reads as nothing rather than as a broken marker: a
 * row written by a newer client must not break an older one's transcript.
 */
export function contextSwitchFromMetadata(message: {
  metadata?: Record<string, unknown>;
}): ContextSwitch | undefined {
  if (message.metadata?.kind !== 'context_switch') return undefined;
  const raw = message.metadata?.context;
  if (!raw || typeof raw !== 'object') return undefined;
  const context = raw as Record<string, unknown>;
  if (!isKind(context.kind)) return undefined;
  return {
    kind: context.kind,
    id: optionalString(context.id),
    title: optionalString(context.title),
    workflow_id: optionalString(context.workflow_id),
  };
}
